import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardList, Plus, Search } from 'lucide-react';
import { inquiriesApi, type InquiryListItem, type InquiryStatus } from '@/lib/api/inquiries.js';
import { formatCurrency, formatRelativeTime } from '@/lib/format.js';
import { Pagination } from '@/components/common/Pagination.js';

type StatusFilter =
  | 'active'
  | InquiryStatus
  | 'follow_up_due';

const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  quoted: 'Quoted',
  follow_up: 'Follow-up',
  converted: 'Converted',
  lost: 'Lost',
  cancelled: 'Cancelled',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-purple-100 text-purple-800',
  quoted: 'bg-amber-100 text-amber-800',
  follow_up: 'bg-orange-100 text-orange-800',
  converted: 'bg-green-100 text-green-800',
  lost: 'bg-slate-200 text-slate-700',
  cancelled: 'bg-slate-200 text-slate-500',
};

function deviceLabel(row: InquiryListItem) {
  const parts = [row.deviceMake, row.deviceModel].filter(Boolean);
  return parts.length ? parts.join(' ') : '—';
}

function isOverdue(row: InquiryListItem) {
  if (!row.followUpAt) return false;
  if (['converted', 'lost', 'cancelled'].includes(row.status)) return false;
  return new Date(row.followUpAt).getTime() < Date.now();
}

export function InquiriesListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [source, setSource] = useState<string>('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, source]);

  const statsQuery = useQuery({
    queryKey: ['inquiries-stats'],
    queryFn: inquiriesApi.stats,
    refetchInterval: 60_000,
  });

  const listQuery = useQuery({
    queryKey: ['inquiries', search, statusFilter, source, page],
    queryFn: () => {
      const params: Parameters<typeof inquiriesApi.list>[0] = {
        search: search.trim() || undefined,
        page,
        pageSize,
      };
      if (source) params.source = source as 'phone' | 'walk_in' | 'online' | 'email';
      if (statusFilter === 'active') {
        // default — API excludes converted/cancelled
      } else if (statusFilter === 'follow_up_due') {
        params.status = 'follow_up,new,contacted,quoted';
      } else if (statusFilter === 'converted' || statusFilter === 'lost') {
        params.status = statusFilter;
        params.includeInactive = true;
      } else {
        params.status = statusFilter;
      }
      return inquiriesApi.list(params);
    },
  });

  const stats = statsQuery.data?.data;
  const rows = useMemo(() => {
    const data = listQuery.data?.data ?? [];
    if (statusFilter !== 'follow_up_due') return data;
    return data.filter(isOverdue);
  }, [listQuery.data?.data, statusFilter]);
  const meta = listQuery.data?.meta;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-950">Inquiries & Quotes</h1>
          <p className="text-sm text-slate-600">Phone quotes and walk-in leads</p>
        </div>
        <Link
          to="/inquiries/new"
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
        >
          <Plus className="h-4 w-4" />
          New Inquiry
        </Link>
      </header>

      {stats ? (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-7">
          {(
            [
              ['new', stats.byStatus.new],
              ['contacted', stats.byStatus.contacted],
              ['quoted', stats.byStatus.quoted],
              ['follow_up', stats.byStatus.follow_up],
              ['overdue', stats.overdueFollowUps],
              ['converted', stats.byStatus.converted],
              ['lost', stats.byStatus.lost],
            ] as const
          ).map(([key, val]) => (
            <div key={key} className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-semibold uppercase text-slate-500">
                {key === 'overdue' ? 'Follow-up Due' : STATUS_LABELS[key]}
              </p>
              <p className="text-xl font-bold text-slate-950">{val ?? 0}</p>
            </div>
          ))}
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm sm:col-span-3 lg:col-span-7">
            <p className="text-sm text-slate-600">
              Conversion rate: <span className="font-bold text-slate-950">{stats.conversionRate}%</span>
              {' · '}
              Today: {stats.todayInquiries} · This week: {stats.weekInquiries}
            </p>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {(
          [
            ['active', 'All Active'],
            ['new', 'New'],
            ['contacted', 'Contacted'],
            ['quoted', 'Quoted'],
            ['follow_up_due', 'Follow-up Due'],
            ['converted', 'Converted'],
            ['lost', 'Lost'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
              statusFilter === key ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'
            }`}
            onClick={() => setStatusFilter(key)}
          >
            {label}
            {key === 'follow_up_due' && stats?.overdueFollowUps ? (
              <span className="ml-1 rounded-full bg-red-600 px-1.5 text-white">
                {stats.overdueFollowUps}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            className="w-full rounded-xl border border-slate-300 py-2 pl-10 pr-3 text-sm outline-none focus:border-primary"
            placeholder="Search name, phone, INQ #..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          value={source}
          onChange={(e) => setSource(e.target.value)}
        >
          <option value="">All sources</option>
          <option value="phone">Phone</option>
          <option value="walk_in">Walk-in</option>
          <option value="online">Online</option>
          <option value="email">Email</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">INQ #</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Device</th>
              <th className="px-4 py-3">Services</th>
              <th className="px-4 py-3">Quoted</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Follow-up</th>
              <th className="px-4 py-3">Assigned</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr
                key={row.id}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => navigate(`/inquiries/${row.id}`)}
              >
                <td className="px-4 py-3 font-semibold text-primary">{row.inquiryNumber}</td>
                <td className="px-4 py-3">
                  <p className="font-medium text-slate-900">{row.customerName ?? '—'}</p>
                  <p className="text-xs text-slate-500">{row.customerPhone ?? ''}</p>
                </td>
                <td className="px-4 py-3 text-slate-700">{deviceLabel(row)}</td>
                <td className="max-w-[180px] truncate px-4 py-3 text-slate-600">
                  {row.servicesSummary || '—'}
                </td>
                <td className="px-4 py-3 font-semibold">{formatCurrency(row.quotedTotal)}</td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[row.status] ?? 'bg-slate-100'}`}
                  >
                    {STATUS_LABELS[row.status] ?? row.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {row.followUpAt ? (
                    <span className={isOverdue(row) ? 'font-semibold text-red-600' : 'text-slate-600'}>
                      {formatRelativeTime(row.followUpAt)}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-4 py-3 text-slate-600">{row.assignedToName ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!listQuery.isLoading && rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-slate-500">
            <ClipboardList className="h-10 w-10 opacity-40" />
            <p>No inquiries found</p>
          </div>
        ) : null}
      </div>

      <Pagination
        page={page}
        pageSize={meta?.pageSize ?? pageSize}
        total={meta?.total ?? 0}
        onPageChange={setPage}
      />
    </div>
  );
}

export function isInquiryOverdue(row: InquiryListItem) {
  return isOverdue(row);
}
